import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import MapStaticToInteractive from '@/app/_components/widgets/map';
import { MapProps } from '@/app/_components/widgets/map/interactive';
import clsx from 'clsx';
import MapIcon from '@heroicons/react/20/solid/MapIcon';
import { MapViewportChangedMessageData, PopupBookCtaClickedMessageData } from '@/app/_components/widgets/map/interactive/types';
import { generateMapUuid, MapMarkerItem } from '@/utils/maps';
import { useFocusTrap } from '@/app/_hooks/useFocusTrap';
import { AvailabilityModalProps, AvailabilityModal } from './card/_components/AvailabilityModal';
import { getStartOfDayTime } from './_utils/time';
import { SlotAvailability } from './types';
import { formatPrice, getCheckoutURL } from '@/utils/checkout';
import { fireConfirmTimeSelectionEvent } from '@/utils/trackingGtm';
import { ROUTES } from '@/routes';
import { delay } from '@/utils/common';
import { useRouter } from 'next/navigation';
import ListingHeader from '@/app/(booking)/cerca/_components/ListingHeader';
import { useIsDesktop } from '@/app/_hooks/useIsScreen';
import Image from 'next/image';
import { DAVINCI_CDN } from '@/app/_constants/defaultValues';

const THRESHOLD_NEW_SEARCH_CTA_DISTANCE = 5000; // in meters

type ListingMapProps = MapProps & {
  onNewSearchInArea?: (newCenter: { lat: number; lng: number }) => Promise<void>;
  location?: string;
  service?: string;
  onBackClick?: () => void;
  onActiveFilterRemove?: () => void;
};

const ListingMap: React.FC<ListingMapProps> = ({
  items,
  markerLabelType,
  setUserGeolocation,
  isUserGeolocationActive,
  hoveredCardId,
  onNewSearchInArea,
  onMarkerFocusChanged,
  onPopupOpened,
  location,
  service,
  onBackClick,
  onActiveFilterRemove,
}) => {
  const router = useRouter();
  const isDesktopAndSmaller = useIsDesktop({ andSmaller: true });

  const [isNewSearchVisible, setIsNewSearchVisible] = useState(false);
  const [isSearchInAreaLoading, setIsSearchInAreaLoading] = useState(false);
  const [selectedItem, setSelectedItem] = useState<MapMarkerItem | undefined>(undefined);
  const [isAvailabilityModalOpenInfo, setIsAvailabilityModalOpenInfo] = useState<null | Omit<
    AvailabilityModalProps,
    'onClose' | 'onSubmit'
  >>(null);

  const [newSearchCenter, setNewSearchCenter] = useState<{ lat: number; lng: number } | null>(null);
  const [newSearchZoom, setNewSearchZoom] = useState<number | null>(null);
  const [mapViewOverride, setMapViewOverride] = useState<{ center: { lat: number; lng: number }; zoom: number | null } | null>(null);
  const [mapMarkerLabelType, setMapMarkerLabelType] = useState(markerLabelType);

  useEffect(() => {
    setMapMarkerLabelType(markerLabelType);
  }, [markerLabelType]);

  const availabilityModalWrapperRef = useRef<HTMLDivElement>(null);

  const mapUuid = useMemo(() => generateMapUuid(items), [items]);
  const isNewSearchEnabled = useMemo(
    () => isNewSearchVisible && !!newSearchCenter && !isSearchInAreaLoading,
    [isNewSearchVisible, newSearchCenter, isSearchInAreaLoading],
  );

  const onMapViewportChanged = useCallback((data: MapViewportChangedMessageData) => {
    setIsNewSearchVisible(data.distanceDelta > THRESHOLD_NEW_SEARCH_CTA_DISTANCE);
    setNewSearchCenter(data.newCenter);
    setNewSearchZoom(data.zoom);
  }, []);

  const onNewSearchClick = async () => {
    if (!onNewSearchInArea || !newSearchCenter || isSearchInAreaLoading) return;

    setMapViewOverride({ center: newSearchCenter, zoom: newSearchZoom });
    setIsSearchInAreaLoading(true);
    try {
      await onNewSearchInArea(newSearchCenter);
    } finally {
      setIsSearchInAreaLoading(false);
    }
  };

  const onViewOverrideApplied = () => setMapViewOverride(null);

  const onPopupBookCtaClicked = (data: PopupBookCtaClickedMessageData) => {
    const item = items.find(i => i.objectID === data.markerData.id);
    setSelectedItem(item);

    if (!item || !item.specializations.id) return;

    const selectedDoctor = item.specializations.doctors?.sort(
      (a, b) => (a.nextAvailability ?? Infinity) - (b.nextAvailability ?? Infinity),
    )?.[0];

    setIsAvailabilityModalOpenInfo({
      specialityId: item.specializations.id,
      doctors: item.specializations.doctors,
      workgroupId: item.ambulatoryId,
      initialSelectedDay: null,
      firstDayExpected: selectedDoctor?.nextAvailability ? getStartOfDayTime(selectedDoctor.nextAvailability) : null,
      delayAvailability: item.delayAvailability,
    });
  };

  const handleSubmit = useCallback(
    async (selectedSlot: SlotAvailability) => {
      if (!selectedSlot) return;
      const selectedDoctor = selectedItem?.specializations.doctors?.sort(
        (a, b) => (a.nextAvailability ?? Infinity) - (b.nextAvailability ?? Infinity),
      )?.[0];
      const formattedPrice = formatPrice({ amount: selectedSlot.price ?? selectedDoctor?.minPrice ?? selectedDoctor?.price ?? 0 });

      fireConfirmTimeSelectionEvent(formattedPrice, {
        item_name: selectedItem?.specializations?.label,
        item_brand: selectedItem?.name,
        item_variant: selectedItem?.specializations?.label,
        item_category: selectedItem?.address ?? undefined,
        price: formattedPrice,
        index: 0,
      });

      const checkoutUrl = getCheckoutURL(ROUTES.CHECKOUT_RECAP.pathname, {
        slot: selectedSlot.id,
      });

      router.push(checkoutUrl);
      await delay(3000);
    },
    [selectedItem, router],
  );

  useFocusTrap({
    trapRef: availabilityModalWrapperRef,
    enabled: !!isAvailabilityModalOpenInfo,
    onClose: () => setIsAvailabilityModalOpenInfo(null),
  });

  useEffect(() => {
    setIsNewSearchVisible(false);
    setNewSearchCenter(null);
    setNewSearchZoom(null);
  }, [mapUuid]);

  useEffect(() => {
    if (mapViewOverride && items.length === 0) setMapViewOverride(null);
  }, [items.length, mapViewOverride]);

  return (
    <>
      {isAvailabilityModalOpenInfo && (
        <div ref={availabilityModalWrapperRef}>
          <AvailabilityModal
            {...isAvailabilityModalOpenInfo}
            onClose={() => setIsAvailabilityModalOpenInfo(null)}
            onSubmit={handleSubmit}
          />
        </div>
      )}

      <div className="flex h-full max-h-dvh w-full flex-col">
        {isDesktopAndSmaller && (
          <ListingHeader
            service={service}
            location={location}
            onBackClick={onBackClick}
            onActiveFilterRemove={onActiveFilterRemove}
            mapMarkerLabelType={mapMarkerLabelType}
            setMapMarkerLabelType={setMapMarkerLabelType}
          />
        )}

        <div className="relative min-h-0 flex-1">
          <button
            type="button"
            aria-label="Cerca in questa area"
            disabled={!isNewSearchEnabled || isSearchInAreaLoading}
            aria-hidden={!isNewSearchVisible}
            tabIndex={isNewSearchVisible ? 0 : -1}
            className={clsx(
              'absolute left-1/2 top-5 z-10 flex -translate-x-1/2 items-center gap-3 whitespace-nowrap rounded-full bg-secondary px-7 py-3 text-base font-semibold text-white transition-all hover:bg-secondary-hover active:bg-[#073E3D] disabled:cursor-not-allowed lg:top-10',
              isNewSearchVisible ? 'opacity-100' : 'pointer-events-none opacity-0',
              isSearchInAreaLoading && 'cursor-wait',
            )}
            onClick={onNewSearchClick}
          >
            <MapIcon className="size-5" />
            Cerca in questa area
          </button>

          {items.length ? (
            <MapStaticToInteractive
              items={items}
              markerLabelType={isDesktopAndSmaller ? mapMarkerLabelType : markerLabelType}
              setUserGeolocation={setUserGeolocation}
              isUserGeolocationActive={isUserGeolocationActive}
              hoveredCardId={hoveredCardId}
              viewOverride={mapViewOverride}
              onMapViewportChanged={onMapViewportChanged}
              onMarkerFocusChanged={onMarkerFocusChanged}
              onPopupBookCtaClicked={onPopupBookCtaClicked}
              onPopupOpened={onPopupOpened}
              onViewOverrideApplied={onViewOverrideApplied}
            />
          ) : (
            <div className="flex h-full w-full items-center justify-center lg:hidden">
              <Image
                className="mx-auto"
                src={`${DAVINCI_CDN}/elty/no-result.jpg`}
                height={400}
                width={400}
                alt="Nessun risultato trovato"
              />
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default ListingMap;
